/** Date-only math for a thread's follow-up schedule (follow_up_at is a YYYY-MM-DD date). */

const DAY_MS = 24 * 60 * 60 * 1000

function dayNumber(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number)
  return Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1) / DAY_MS
}

/** Today's date in the viewer's own calendar, as YYYY-MM-DD. */
export function localToday(now: Date = new Date()): string {
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${m}-${d}`
}

/** Whole calendar days from `today` to `date` (both YYYY-MM-DD); negative when past. */
export function daysUntil(date: string, today: string): number {
  return Math.round(dayNumber(date) - dayNumber(today))
}

export function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

export type FollowUpTone = 'upcoming' | 'today' | 'overdue' | 'none'

/** What the follow-up button says: when the next follow-up goes out, in plain words. */
export function followUpLabel(
  followUpAt: string | null,
  today: string = localToday(),
): { tone: FollowUpTone; text: string } {
  if (!followUpAt) return { tone: 'none', text: 'No follow-up scheduled' }
  const days = daysUntil(followUpAt, today)
  if (days > 1) return { tone: 'upcoming', text: `Next follow-up in ${days} days` }
  if (days === 1) return { tone: 'upcoming', text: 'Next follow-up tomorrow' }
  if (days === 0) return { tone: 'today', text: 'Next follow-up today' }
  const late = -days
  return { tone: 'overdue', text: `Follow-up overdue by ${late} day${late === 1 ? '' : 's'}` }
}
